import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Select from 'react-select';

import Spinner from '../../reusable/spinner/spinner.component';
import Client from '../../../tools/client.js';

import {
  MainContainer,
  Title
} from '../../../styles/component.styles.jsx';

const client = new Client();

const periodOptions = [
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
  { value: 'quarter', label: 'Quarterly' },
  { value: 'year', label: 'Yearly' },
];

const periodLabels = {
  week: { current: 'This Week', previous: 'Last Week' },
  month: { current: 'This Month', previous: 'Last Month' },
  quarter: { current: 'This Quarter', previous: 'Last Quarter' },
  year: { current: 'This Year', previous: 'Last Year' },
};

const formatCurrency = (cents) => `$${(cents / 100).toFixed(2)}`;

const getChange = (current, previous) => {
  if (previous === 0) return null;
  const pct = ((current - previous) / previous * 100).toFixed(1);
  return { pct: parseFloat(pct), label: `${pct >= 0 ? '+' : ''}${pct}%` };
};

const SelectorRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1rem;
  width: 100%;
  max-width: 200px;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
  width: 100%;
  max-width: 600px;
  padding: 0 0.5rem;

  @media (min-width: 480px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (min-width: 760px) {
    grid-template-columns: 1fr 1fr 1fr;
    max-width: 720px;
  }
`;

const Card = styled.div`
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  text-align: center;
`;

const CardLabel = styled.h4`
  margin: 0 0 0.25rem 0;
  color: #555;
  font-size: 0.85rem;
`;

const CardValue = styled.p`
  margin: 0;
  font-size: ${({ small }) => small ? '1.15rem' : '1.4rem'};
  font-weight: bold;
`;

const CardSub = styled.p`
  margin: 0;
  color: #777;
  font-size: 0.75rem;
`;

const Badge = styled.span`
  display: inline-block;
  margin-top: 0.25rem;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: bold;
  color: ${({ positive }) => (positive ? '#15803d' : '#dc2626')};
  background: ${({ positive }) => (positive ? '#dcfce7' : '#fee2e2')};
`;

const MoMSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  margin-top: 1rem;
  font-size: 0.9rem;
`;

const MoMRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StatCard = ({ label, orders, revenue, change }) => (
  <Card>
    <CardLabel>{label}</CardLabel>
    <CardValue>{orders}</CardValue>
    <CardSub>orders</CardSub>
    <CardValue small>{formatCurrency(revenue)}</CardValue>
    <CardSub>revenue</CardSub>
    {change && (
      <Badge positive={change.pct >= 0}>{change.label}</Badge>
    )}
  </Card>
);

const MetricsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [period, setPeriod] = useState({ value: 'month', label: 'Monthly' });

  useEffect(() => {
    const fetchDashboard = async () => {
      setLoading(true);
      const res = await client.getMetricsDashboard(period.value);
      setDashboard(res);
      setLoading(false);
    };
    fetchDashboard();
  }, [period]);

  const labels = periodLabels[period.value];

  return (
    <MainContainer>
      <Title>Dashboard</Title>

      <SelectorRow>
        <Select
          value={period}
          onChange={setPeriod}
          options={periodOptions}
          isSearchable={false}
          styles={{ container: (base) => ({ ...base, width: '100%' }) }}
        />
      </SelectorRow>

      {loading ? (
        <Spinner />
      ) : !dashboard ? (
        <p>Failed to load dashboard data.</p>
      ) : (
        <>
          <CardGrid>
            <StatCard label="All Time" orders={dashboard.allTime.orders} revenue={dashboard.allTime.revenue} />
            <StatCard label={labels.current} orders={dashboard.current.orders} revenue={dashboard.current.revenue} />
            <StatCard label={labels.previous} orders={dashboard.previous.orders} revenue={dashboard.previous.revenue} />
          </CardGrid>

          <MoMSection>
            <MoMRow>
              <span>Orders vs {labels.previous.toLowerCase()}:</span>
              {getChange(dashboard.current.orders, dashboard.previous.orders) ? (
                <Badge positive={getChange(dashboard.current.orders, dashboard.previous.orders).pct >= 0}>
                  {getChange(dashboard.current.orders, dashboard.previous.orders).label}
                </Badge>
              ) : (
                <span style={{ color: '#888' }}>N/A</span>
              )}
            </MoMRow>
            <MoMRow>
              <span>Revenue vs {labels.previous.toLowerCase()}:</span>
              {getChange(dashboard.current.revenue, dashboard.previous.revenue) ? (
                <Badge positive={getChange(dashboard.current.revenue, dashboard.previous.revenue).pct >= 0}>
                  {getChange(dashboard.current.revenue, dashboard.previous.revenue).label}
                </Badge>
              ) : (
                <span style={{ color: '#888' }}>N/A</span>
              )}
            </MoMRow>
          </MoMSection>
        </>
      )}
    </MainContainer>
  );
};

export default MetricsDashboard;
